import type { FilterState } from '../contexts/SimpleFilterContext';

// Type for reference data structure (adapt this to match your actual data structure)
export interface FilterableReference {
  _id: string;
  title: string;
  description?: string;
  tags: string[];
  [key: string]: any; // Allow additional properties
}

/**
 * Apply filters to reference data based on the current filter state
 */
export function applySimpleFilters<T extends FilterableReference>(
  references: T[],
  filterState: FilterState
): T[] {
  // First filter by search term
  let filtered = references;
  
  if (filterState.searchTerm.trim()) {
    const term = filterState.searchTerm.toLowerCase().trim();
    filtered = filtered.filter(ref => {
      const searchableText = [
        ref.title,
        ref.description || '',
        ...ref.tags
      ].join(' ').toLowerCase();
      
      return searchableText.includes(term);
    });
  }
  
  // No tag filters? Return search results
  if (Object.keys(filterState.tags).length === 0) {
    return filtered;
  }
  
  // Group tags by their logic mode
  const tagsByMode = Object.values(filterState.tags).reduce(
    (acc, tag) => {
      acc[tag.mode].push(tag.value);
      return acc;
    },
    { inclusive: [] as string[], required: [] as string[], excluded: [] as string[] }
  );
  
  return filtered.filter(ref => {
    // First, exclude items with any excluded tags
    if (tagsByMode.excluded.some(tag => ref.tags.includes(tag))) {
      return false;
    }
    
    // Check required tags (AND logic) - all must be present
    if (tagsByMode.required.length > 0) {
      const hasAllRequiredTags = tagsByMode.required.every(tag => ref.tags.includes(tag));
      if (!hasAllRequiredTags) {
        return false;
      }
    }
    
    // Check inclusive tags (OR logic) - at least one must be present (if any inclusive tags exist)
    if (tagsByMode.inclusive.length > 0) {
      const hasAnyInclusiveTags = tagsByMode.inclusive.some(tag => ref.tags.includes(tag));
      if (!hasAnyInclusiveTags) {
        return false;
      }
    }
    
    return true;
  });
}

/**
 * Get a summary of active filters for display purposes
 */
export function getFilterSummary(filterState: FilterState): {
  searchTerm: string;
  tagCounts: { inclusive: number; required: number; excluded: number };
  totalTags: number;
  hasFilters: boolean;
} {
  const tagCounts = Object.values(filterState.tags).reduce(
    (acc, tag) => {
      acc[tag.mode]++;
      return acc;
    },
    { inclusive: 0, required: 0, excluded: 0 }
  );
  
  const totalTags = Object.keys(filterState.tags).length;
  const hasFilters = totalTags > 0 || filterState.searchTerm.trim().length > 0;
  
  return {
    searchTerm: filterState.searchTerm,
    tagCounts,
    totalTags,
    hasFilters
  };
}

/**
 * Generate a human-readable description of the current filters
 */
export function getFilterDescription(filterState: FilterState): string {
  const { searchTerm, tags, globalLogic } = filterState;
  const parts: string[] = [];
  
  if (searchTerm.trim()) {
    parts.push(`containing "${searchTerm}"`);
  }
  
  const tagsByMode = Object.values(tags).reduce(
    (acc, tag) => {
      acc[tag.mode].push(tag.value);
      return acc;
    },
    { inclusive: [] as string[], required: [] as string[], excluded: [] as string[] }
  );
  
  if (tagsByMode.inclusive.length > 0) {
    parts.push(`with any of: ${tagsByMode.inclusive.join(', ')}`);
  }
  
  if (tagsByMode.required.length > 0) {
    parts.push(`must have: ${tagsByMode.required.join(', ')}`);
  }
  
  if (tagsByMode.excluded.length > 0) {
    parts.push(`excluding: ${tagsByMode.excluded.join(', ')}`);
  }
  
  if (parts.length === 0) {
    return 'No filters applied';
  }
  
  if (parts.length === 1) {
    return parts[0];
  }
  
  // Join with appropriate connectors based on global logic
  const connector = globalLogic === 'AND' ? ' and ' : ' or ';
  return parts.join(connector);
}

/**
 * Check if a specific tag is currently active in any mode
 */
export function isTagActive(filterState: FilterState, tagValue: string): {
  active: boolean;
  mode?: 'inclusive' | 'required' | 'excluded';
  tagId?: string;
} {
  for (const [tagId, tag] of Object.entries(filterState.tags)) {
    if (tag.value === tagValue) {
      return { active: true, mode: tag.mode, tagId };
    }
  }
  return { active: false };
}

/**
 * Get the next mode for a tag when cycling through states
 */
export function getNextTagMode(currentMode?: 'inclusive' | 'required' | 'excluded'): 'inclusive' | 'required' | 'excluded' | null {
  if (!currentMode) return 'inclusive';
  
  const nextMode = {
    inclusive: 'required' as const,
    required: 'excluded' as const,
    excluded: null // Remove the tag
  };
  
  return nextMode[currentMode];
}

/**
 * Generate a unique tag ID for a given tag value and mode
 */
export function generateTagId(value: string, mode: 'inclusive' | 'required' | 'excluded'): string {
  return `${mode}-${value}`;
}

/**
 * Check if any filters are currently active
 */
export function hasActiveFilters(filterState: FilterState): boolean {
  return Object.keys(filterState.tags).length > 0 || filterState.searchTerm.trim().length > 0;
} 